import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Receipt, Plus, Trash2, Edit3, RefreshCw, ExternalLink, Send, Copy, Check, FileCheck2, FileText,
} from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';
import LetterheadManager from './LetterheadManager';
import SendDocumentDialog from './SendDocumentDialog';
import StructuredEstimateForm from './StructuredEstimateForm';

const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return iso || '—'; } };
const blankLine = () => ({ label: '', qty: 1, unit_price: 0 });

const statusBadge = (s) => {
  const map = {
    draft: 'bg-slate-700/60 text-gray-200 border-slate-600',
    sent: 'bg-blue-600/20 text-blue-300 border-blue-600/30',
    accepted: 'bg-green-600/20 text-green-300 border-green-600/30',
    declined: 'bg-red-600/20 text-red-300 border-red-600/30',
  };
  return map[s] || map.draft;
};

const EstimateBuilder = ({ open, onClose, initial, templates, letterheads, onSaved }) => {
  const [form, setForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '', customer_address: '',
    tax_rate: 0, notes: '', valid_until: '', letterhead_id: '',
  });
  const [items, setItems] = useState([blankLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        customer_name: initial.customer_name || '',
        customer_email: initial.customer_email || '',
        customer_phone: initial.customer_phone || '',
        customer_address: initial.customer_address || '',
        tax_rate: initial.tax_rate ?? 0,
        notes: initial.notes || '',
        valid_until: initial.valid_until || '',
        letterhead_id: initial.letterhead_id || '',
      });
      setItems(initial.line_items?.length ? initial.line_items.map((i) => ({ ...i })) : [blankLine()]);
    } else {
      setForm({ customer_name: '', customer_email: '', customer_phone: '', customer_address: '', tax_rate: 0, notes: '', valid_until: '', letterhead_id: '' });
      setItems([blankLine()]);
    }
  }, [open, initial]);

  const applyTemplate = (tplId) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    setItems(tpl.line_items.map((it) => ({
      label: it.label, qty: it.default_qty || 1, unit_price: it.unit_price || 0,
    })));
    if (tpl.notes && !form.notes) {
      setForm((f) => ({ ...f, notes: tpl.notes }));
    }
    toast.success(`Loaded "${tpl.name}"`);
  };

  const updateItem = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const addItem = () => setItems((prev) => [...prev, blankLine()]);

  const subtotal = items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const taxAmount = subtotal * ((parseFloat(form.tax_rate) || 0) / 100);
  const total = subtotal + taxAmount;

  const save = async () => {
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return; }
    const cleanItems = items
      .filter((it) => (it.label || '').trim())
      .map((it) => ({ label: it.label.trim(), qty: parseFloat(it.qty) || 1, unit_price: parseFloat(it.unit_price) || 0 }));
    if (cleanItems.length === 0) { toast.error('Add at least one line item'); return; }

    setSaving(true);
    try {
      const body = {
        ...form,
        customer_email: form.customer_email.trim() || null,
        tax_rate: parseFloat(form.tax_rate) || 0,
        line_items: cleanItems,
        valid_until: form.valid_until || null,
        letterhead_id: form.letterhead_id || null,
      };
      if (initial?.id) {
        await axios.patch(`${API}/api/estimates/${initial.id}`, body, { headers: authHeaders() });
        toast.success('Estimate updated');
      } else {
        await axios.post(`${API}/api/estimates`, body, { headers: authHeaders() });
        toast.success('Estimate created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <img
              src="/logo-taylordarms-oval.png"
              alt="TayLord Arms"
              data-testid="estimate-builder-logo"
              className="w-12 h-12 object-contain flex-shrink-0 rounded-full bg-white/5 border border-slate-700 p-1"
            />
            <div className="flex-1 min-w-0">
              <DialogTitle data-testid="estimate-builder-title">{initial?.id ? `Edit ${initial.estimate_number}` : 'New estimate'}</DialogTitle>
              <DialogDescription className="text-gray-400">
                {initial?.id ? 'Edit line items and totals. Status changes are saved instantly.' : 'Build a professional estimate to send to your customer.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {templates.length > 0 && !initial?.id && (
            <div className="rounded border border-amber-700/30 bg-amber-950/15 p-3">
              <Label className="text-amber-200 text-xs">Quick-fill from template</Label>
              <Select onValueChange={applyTemplate}>
                <SelectTrigger data-testid="estimate-template-picker" className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue placeholder="Pick a template…" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-white">
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300 text-xs">Customer name *</Label>
              <Input data-testid="estimate-customer-name" value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Customer email</Label>
              <Input data-testid="estimate-customer-email" type="email" value={form.customer_email} onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Phone</Label>
              <Input value={form.customer_phone} onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Valid until</Label>
              <Input type="date" value={form.valid_until} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-gray-300 text-xs">Address (optional)</Label>
              <Input value={form.customer_address} onChange={(e) => setForm((f) => ({ ...f, customer_address: e.target.value }))} placeholder="123 Main St, Moncks Corner, SC 29461" className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-gray-300 text-xs">Letterhead</Label>
              <Select value={form.letterhead_id || 'default'} onValueChange={(v) => setForm((f) => ({ ...f, letterhead_id: v === 'default' ? '' : v }))}>
                <SelectTrigger data-testid="estimate-letterhead-picker" className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-white">
                  <SelectItem value="default">Default — TayLord Arms branded</SelectItem>
                  {letterheads.map((lh) => (
                    <SelectItem key={lh.id} value={lh.id}>{lh.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {letterheads.length === 0 && (
                <p className="text-[11px] text-gray-500 mt-1">No custom letterheads yet — upload one from the &ldquo;Manage letterheads&rdquo; button above.</p>
              )}
            </div>
          </div>

          <div>
            <Label className="text-gray-300 text-xs">Line items *</Label>
            <div className="space-y-2 mt-1">
              {items.map((it, idx) => (
                <div key={`line-${idx}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`estimate-line-${idx}`}>
                  <Input placeholder="Service or item description" value={it.label} onChange={(e) => updateItem(idx, { label: e.target.value })} className="col-span-7 bg-slate-800 border-slate-700 text-white" />
                  <Input type="number" step="0.5" min="0" placeholder="Qty" value={it.qty} onChange={(e) => updateItem(idx, { qty: e.target.value })} className="col-span-2 bg-slate-800 border-slate-700 text-white tabular-nums" />
                  <Input type="number" step="0.01" min="0" placeholder="Price" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} className="col-span-2 bg-slate-800 border-slate-700 text-white tabular-nums" />
                  <Button type="button" size="sm" variant="outline" onClick={() => removeItem(idx)} disabled={items.length === 1} className="col-span-1 border-slate-700 text-gray-400 hover:bg-red-950/40 hover:text-red-400 px-2" aria-label="Remove line">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="estimate-add-line" className="mt-2 border-slate-700 text-gray-300 hover:bg-slate-800">
              <Plus className="w-3 h-3 mr-1" /> Add line
            </Button>
          </div>

          {/* Totals */}
          <div className="rounded border border-slate-700 bg-slate-950/40 p-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-300">Subtotal</span>
              <span className="text-white font-semibold tabular-nums">{usd(subtotal)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-300">Tax rate (%)</span>
              <Input
                type="number" step="0.1" min="0" max="100"
                data-testid="estimate-tax-rate"
                value={form.tax_rate}
                onChange={(e) => setForm((f) => ({ ...f, tax_rate: e.target.value }))}
                className="w-24 bg-slate-800 border-slate-700 text-white text-right tabular-nums"
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Tax</span>
              <span className="text-gray-200 tabular-nums">{usd(taxAmount)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-slate-700 pt-2 text-base">
              <span className="text-white font-semibold">Total</span>
              <span className="text-amber-300 font-bold tabular-nums" data-testid="estimate-total">{usd(total)}</span>
            </div>
          </div>

          <div>
            <Label className="text-gray-300 text-xs">Notes / terms (shown to customer)</Label>
            <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={3} className="bg-slate-800 border-slate-700 text-white text-sm" placeholder="Turnaround 5-7 business days. 50% deposit due at drop-off." />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-800">
          <Button variant="outline" onClick={onClose} className="border-slate-700 text-gray-300 hover:bg-slate-800">Cancel</Button>
          <Button data-testid="estimate-save" onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white">
            {saving ? 'Saving…' : initial?.id ? 'Save changes' : 'Create estimate'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AdminEstimates = ({ onUnauthorized }) => {
  const [estimates, setEstimates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [letterheads, setLetterheads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [structuredOpen, setStructuredOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [lhManagerOpen, setLhManagerOpen] = useState(false);
  const [sending, setSending] = useState(null); // estimate currently being emailed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, t, lh] = await Promise.all([
        axios.get(`${API}/api/estimates`, { headers: authHeaders() }),
        axios.get(`${API}/api/quote-templates`, { headers: authHeaders() }),
        axios.get(`${API}/api/letterheads`, { headers: authHeaders() }),
      ]);
      setEstimates(e.data);
      setTemplates(t.data);
      setLetterheads(lh.data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error(`Failed to load: ${apiErr(err)}`);
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const remove = async (est) => {
    const ok = window.confirm(`Delete ${est.estimate_number} (${est.customer_name})?\n\nThis cannot be undone.`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/estimates/${est.id}`, { headers: authHeaders() });
      toast.success('Estimate deleted');
      load();
    } catch (err) {
      toast.error(`Delete failed: ${apiErr(err)}`);
    }
  };

  const updateStatus = async (est, status) => {
    try {
      await axios.patch(`${API}/api/estimates/${est.id}`, { status }, { headers: authHeaders() });
      // When marked accepted, offer to log the payment to the ledger
      if (status === 'accepted') {
        toast.success(`Marked accepted — log ${usd(est.total)} as income?`, {
          duration: 8000,
          action: {
            label: 'Log payment',
            onClick: () => {
              // Tell the parent Admin shell to switch to the Ledger tab
              window.dispatchEvent(new CustomEvent('admin:goto-tab', { detail: 'ledger' }));
              // After a short delay, fire pre-fill event so AdminLedger picks it up
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('ledger:prefill', {
                  detail: {
                    estimate_id: est.id,
                    customer_email: est.customer_email,
                    amount: est.total,
                    note: `Payment for ${est.estimate_number} — ${est.customer_name}`,
                  },
                }));
              }, 300);
            },
          },
        });
      } else {
        toast.success(`Marked ${status}`);
      }
      load();
    } catch (err) {
      toast.error(`Update failed: ${apiErr(err)}`);
    }
  };

  const publicUrl = (est) => `${window.location.origin}/estimate/${est.id}`;

  const copyLink = async (est) => {
    try {
      await navigator.clipboard.writeText(publicUrl(est));
      setCopiedId(est.id);
      setTimeout(() => setCopiedId(null), 1500);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed — long-press the link instead');
    }
  };

  const emailLink = (est) => {
    const subject = encodeURIComponent(`TayLord Arms — Estimate ${est.estimate_number}`);
    const body = encodeURIComponent(
      `Hi ${est.customer_name},\n\n` +
      `Here is your estimate from TayLord Arms LLC:\n` +
      `${publicUrl(est)}\n\n` +
      `Total: ${usd(est.total)}\n\n` +
      `If you have any questions, just reply to this email or give us a call.\n\n` +
      `Thanks,\nTayLord Arms LLC`
    );
    return `mailto:${est.customer_email || ''}?subject=${subject}&body=${body}`;
  };

  // Convert an accepted estimate into a brand new invoice
  const convertToInvoice = async (est) => {
    try {
      const { data: inv } = await axios.post(
        `${API}/api/invoices/from-estimate/${est.id}`,
        {},
        { headers: authHeaders() },
      );
      toast.success(`Converted to ${inv.invoice_number}`, {
        duration: 8000,
        action: {
          label: 'Open invoices →',
          onClick: () => {
            window.dispatchEvent(new CustomEvent('admin:goto-tab', { detail: 'invoices' }));
            // After tab switch, open payment dialog for the new invoice
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('invoice:open-payment', { detail: inv.id }));
            }, 300);
          },
        },
      });
      load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error(err.response.data.detail || 'Already converted');
      } else {
        toast.error(`Convert failed: ${apiErr(err)}`);
      }
    }
  };

  return (
    <section data-testid="admin-estimates-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Receipt className="w-5 h-5 text-amber-400" /> Estimates
          </h2>
          <p className="text-sm text-gray-400">Build customer-ready estimates. Send a private link or print to PDF with full letterhead.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setLhManagerOpen(true)} variant="outline" size="sm" data-testid="estimate-manage-letterheads" className="border-slate-700 text-white hover:bg-slate-800">
            <FileText className="w-3 h-3 mr-2" /> Manage letterheads
          </Button>
          <Button onClick={load} variant="outline" size="sm" className="border-slate-700 text-white hover:bg-slate-800">
            <RefreshCw className="w-3 h-3 mr-2" /> Refresh
          </Button>
          <Button data-testid="estimate-new" onClick={() => { setEditing(null); setStructuredOpen(true); }} className="bg-red-600 hover:bg-red-700 text-white">
            <Plus className="w-3 h-3 mr-2" /> New estimate
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : estimates.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-12 text-center" data-testid="estimates-empty">
            <Receipt className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-300">No estimates yet.</p>
            <p className="text-gray-500 text-sm mt-1">Create one from scratch or use a template for a head start.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white">All estimates ({estimates.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded border border-slate-800">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/40 text-gray-400 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Number</th>
                    <th className="px-3 py-2 font-medium">Customer</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium text-right">Total</th>
                    <th className="px-3 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {estimates.map((est) => (
                    <tr key={est.id} className="border-t border-slate-800 hover:bg-slate-900/70" data-testid={`estimate-row-${est.id}`}>
                      <td className="px-3 py-3 font-mono text-amber-300 whitespace-nowrap">{est.estimate_number}</td>
                      <td className="px-3 py-3">
                        <div className="text-white font-medium">{est.customer_name}</div>
                        {est.customer_email && <div className="text-xs text-red-400">{est.customer_email}</div>}
                      </td>
                      <td className="px-3 py-3 text-gray-300 whitespace-nowrap">{fmt(est.created_at)}</td>
                      <td className="px-3 py-3">
                        <Select value={est.status} onValueChange={(v) => updateStatus(est, v)}>
                          <SelectTrigger data-testid={`estimate-status-${est.id}`} className={`h-7 text-xs border ${statusBadge(est.status)} w-28`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 text-white">
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="sent">Sent</SelectItem>
                            <SelectItem value="accepted">Accepted</SelectItem>
                            <SelectItem value="declined">Declined</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-3 text-right font-bold text-amber-300 tabular-nums">{usd(est.total)}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          {est.status === 'accepted' && (
                            <Button
                              size="sm"
                              data-testid={`estimate-convert-${est.id}`}
                              onClick={() => convertToInvoice(est)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              title="Convert this accepted estimate to an invoice"
                            >
                              <FileCheck2 className="w-3 h-3 mr-1" /> Convert
                            </Button>
                          )}
                          <Button size="sm" variant="outline" data-testid={`estimate-view-${est.id}`} asChild className="border-slate-700 text-gray-200 hover:bg-slate-800">
                            <a href={publicUrl(est)} target="_blank" rel="noopener noreferrer" title="Open public estimate page">
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`estimate-copy-${est.id}`} onClick={() => copyLink(est)} title="Copy public link" className="border-slate-700 text-gray-300 hover:bg-slate-800 px-2">
                            {copiedId === est.id ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`estimate-email-${est.id}`} onClick={() => setSending(est)} title="Email this estimate with PDF attachment" className="border-slate-700 text-gray-300 hover:bg-slate-800 px-2">
                            <Send className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`estimate-edit-${est.id}`} onClick={() => { setEditing(est); setStructuredOpen(true); }} className="border-slate-700 text-gray-300 hover:bg-slate-800 px-2" aria-label="Edit estimate">
                            <Edit3 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`estimate-delete-${est.id}`} onClick={() => remove(est)} className="border-slate-700 text-gray-400 hover:bg-red-950/40 hover:text-red-400 px-2" aria-label="Delete estimate">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <EstimateBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        initial={editing}
        templates={templates}
        letterheads={letterheads}
        onSaved={load}
      />

      <StructuredEstimateForm
        open={structuredOpen}
        onClose={() => setStructuredOpen(false)}
        initial={editing}
        letterheads={letterheads}
        onSaved={load}
      />

      <LetterheadManager
        open={lhManagerOpen}
        onClose={() => setLhManagerOpen(false)}
        onChanged={load}
      />

      <SendDocumentDialog
        open={Boolean(sending)}
        onClose={() => setSending(null)}
        kind="estimate"
        doc={sending}
        onSent={load}
      />
    </section>
  );
};

export default AdminEstimates;
